import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Layers } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatNumber, formatTaka } from '@/utils/number';
import { pcmpUrgentBatchesQueryOptions } from '../../api/pcmpRequests.api';
import { PCMP_CHANNEL_LABELS } from '../../constants/pcmpProposal.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import { PCMP_REQUEST_EMPTY_MESSAGE } from '../../constants/pcmpRequest.constants';
import { toPcmpQueueSearch } from '../../hooks/usePcmpQueueFilters';
import type { PcmpQueueFilters } from '../../types/pcmpQueue.types';
import type { PcmpRequestBatchesParams } from '../../types/pcmpRequest.types';
import type { PcmpUrgentBatch, PcmpUrgentBatches } from '../../types/pcmpUrgent.types';
import {
  PcmpBatchAmountCell,
  PcmpBatchItemsCell,
  PcmpBatchStepCell,
} from '../PcmpBatch/PcmpBatchCells';
import { PcmpBatchStatusPill } from '../PcmpMisBatches/PcmpMisBatchCells';
import { PcmpEmptyCell } from '../PcmpQueue/PcmpQueueCells';

const columns: ColumnDef<PcmpUrgentBatch>[] = [
  {
    id: 'batchNo',
    header: 'Batch',
    cell: ({ row }) => (
      <span className="font-mono font-medium whitespace-nowrap">#{row.original.batchNo}</span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <PcmpBatchStatusPill status={row.original.status} />,
  },
  {
    id: 'channel',
    header: 'Payer',
    cell: ({ row }) =>
      row.original.channel ? PCMP_CHANNEL_LABELS[row.original.channel] : <PcmpEmptyCell />,
  },
  {
    id: 'items',
    header: () => <div className="text-right">Proposals</div>,
    cell: ({ row }) => (
      <div className="text-right">
        <PcmpBatchItemsCell batch={row.original} />
      </div>
    ),
  },
  {
    id: 'amount',
    header: () => <div className="text-right">Amount</div>,
    cell: ({ row }) => (
      <div className="text-right whitespace-nowrap tabular-nums">
        {formatTaka(row.original.totalAmount)}
      </div>
    ),
  },
  {
    id: 'pending',
    header: () => <div className="text-right">Pending</div>,
    cell: ({ row }) => <PcmpBatchAmountCell value={row.original.pending} />,
  },
  {
    id: 'disbursed',
    header: () => <div className="text-right">Disbursed</div>,
    cell: ({ row }) => <PcmpBatchAmountCell value={row.original.disbursed} />,
  },
  {
    id: 'failed',
    header: () => <div className="text-right">Failed</div>,
    cell: ({ row }) => <PcmpBatchAmountCell value={row.original.disbursementFailed} />,
  },
  {
    id: 'closed',
    header: () => <div className="text-right">Rejected / Returned</div>,
    cell: ({ row }) => (
      <PcmpBatchAmountCell
        value={{
          count: row.original.rejected.count + row.original.returned.count,
          amount: row.original.rejected.amount + row.original.returned.amount,
        }}
      />
    ),
  },
  {
    id: 'staged',
    header: 'Staged',
    cell: ({ row }) => (
      <PcmpBatchStepCell by={row.original.createdBy} at={row.original.createdAt} />
    ),
  },
  {
    id: 'published',
    header: 'Published',
    cell: ({ row }) => <PcmpBatchStepCell by={null} at={row.original.publishedAt} />,
  },
];

function TotalsStrip({ totals }: { totals: PcmpUrgentBatches['totals'] }) {
  const items = [
    { label: 'Batches', value: formatNumber(totals.batches), hint: '' },
    {
      label: 'Total',
      value: formatTaka(totals.totalAmount),
      hint: `${formatNumber(totals.itemCount)} proposals`,
    },
    {
      label: 'Disbursed',
      value: formatTaka(totals.disbursed.amount),
      hint: `${formatNumber(totals.disbursed.count)} proposals`,
    },
    {
      label: 'Pending',
      value: formatTaka(totals.pending.amount),
      hint: `${formatNumber(totals.pending.count)} proposals`,
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {items.map(({ label, value, hint }) => (
        <div key={label} className="flex flex-col rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span className="text-lg font-semibold tabular-nums">{value}</span>
          {hint ? <span className="text-xs text-muted-foreground tabular-nums">{hint}</span> : null}
        </div>
      ))}
    </div>
  );
}

/** The urgent batches, newest first; a row opens the batch. Figures are the viewer's share. */
/** What the batch list needs of a screen's filter controller (Request or Requested). */
export interface PcmpUrgentBatchesController {
  batchesParams: PcmpRequestBatchesParams;
  appliedFilters: PcmpQueueFilters;
  limit: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
}

export function PcmpUrgentBatchesView({ controller }: { controller: PcmpUrgentBatchesController }) {
  const router = useRouter();
  const { batchesParams, appliedFilters, limit } = controller;

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpUrgentBatchesQueryOptions(batchesParams),
  );

  useKeepPageInRange(batchesParams.page, data?.batches.meta.totalPages, controller.setPage);

  const openBatch = useCallback(
    (batch: PcmpUrgentBatch) => {
      void router.navigate({
        to: '/pcmp/requests/batches/$batchNo',
        params: { batchNo: batch.batchNo },
        // The batch opens on the members matching the same filters.
        search: toPcmpQueueSearch(appliedFilters),
      });
    },
    [router, appliedFilters],
  );

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load the urgent batches"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        fill
      />
    );
  }

  return (
    <>
      {data ? <TotalsStrip totals={data.totals} /> : null}
      <DataTable
        data={data?.batches.items ?? []}
        columns={columns}
        isLoading={isLoading}
        fillHeight
        className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
        emptyIcon={Layers}
        emptyMessage={PCMP_REQUEST_EMPTY_MESSAGE.BATCHES}
        skeletonRowCount={Math.min(limit, 15)}
        getRowClassName={(batch) => (batch.status === 'STAGED' ? 'bg-warning/30' : undefined)}
        onRowClick={openBatch}
      />
      {data ? (
        <DataTablePagination
          page={data.batches.meta.page}
          totalPages={data.batches.meta.totalPages}
          total={data.batches.meta.total}
          limit={data.batches.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}
    </>
  );
}
